import React from "react";
import { render, screen, waitFor, within } from "@testing-library/react";
import userEvent from "@testing-library/user-event";
import "@testing-library/jest-dom";

const mockNavigate = jest.fn();
jest.mock("react-router-dom", () => ({
  useParams: () => ({ config_id: "config-1", result_id: "result-1" }),
  useNavigate: () => mockNavigate,
  useLocation: () => ({ pathname: "/", search: "", state: null, key: "k" }),
}));

const mockUseWorkspaceContext = jest.fn();
jest.mock("../../../providers/WorkspaceProvider", () => ({
  useWorkspaceContext: () => mockUseWorkspaceContext(),
}));

const mockFetchResult = jest.fn();
jest.mock("../hooks", () => ({
  useDigestResultGet: () => mockFetchResult,
}));

jest.mock("../components/BKGAgent", () => ({
  FloatingChatWidget: () => <div data-testid="floating-chat-widget" />,
}));

const DigestView = require("./DigestView").default;

// Both suites render inside workspace-1 unless a test overrides it.
beforeEach(() => {
  mockUseWorkspaceContext.mockReturnValue({
    workspace: { id: "workspace-1", name: "Workspace" },
  });
});

const baseResult = {
  id: "result-1",
  workspace_id: "workspace-1",
  digest_config_id: "config-1",
  generated_at: "2026-10-12T09:00:00Z",
  generated_by: "scheduled",
  event_count: 2,
  llm_model: "test-model",
  llm_prompt_tokens: 1,
  llm_completion_tokens: 1,
  generation_duration_seconds: 1.5,
  status: "completed",
  error_message: null,
};

const item = (overrides: Record<string, unknown>) => ({
  rank: 1,
  catalyst_event_id: "event-1",
  company_name: "Example Bio",
  title: "Example Bio raises $150M Series B",
  event_type: "Funding Round",
  event_date: "2026-10-08",
  summary: "Summary",
  rationale: "Rationale",
  source_url: null,
  reported_date: null,
  ...overrides,
});

describe("DigestView executive snapshot", () => {
  beforeEach(() => mockFetchResult.mockReset());

  it("shows the snapshot, per-item signals and the sentiment table", async () => {
    mockFetchResult.mockResolvedValue({
      ...baseResult,
      executive_summary: [
        "Example Bio raised $150M.",
        "Other Tx missed Phase 3.",
      ],
      items: [
        item({ sentiment: "positive" }),
        item({
          rank: 2,
          company_name: "Other Tx",
          title: "Other Tx misses Phase 3 endpoint",
          sentiment: "negative",
        }),
        item({
          rank: 3,
          company_name: "Third Co",
          title: "No signal",
          sentiment: null,
        }),
      ],
    });

    render(<DigestView />);

    expect(await screen.findByText("Executive snapshot")).toBeInTheDocument();
    expect(screen.getByText("Example Bio raised $150M.")).toBeInTheDocument();
    expect(screen.getByText("Other Tx missed Phase 3.")).toBeInTheDocument();

    const table = screen.getByRole("table");
    const rows = within(table).getAllByRole("row");
    expect(rows).toHaveLength(3); // header + the two items that carry a signal
    expect(within(rows[1]).getByText("Example Bio")).toBeInTheDocument();
    expect(within(rows[1]).getByText("Positive")).toBeInTheDocument();
    expect(within(rows[2]).getByText("Other Tx")).toBeInTheDocument();
    expect(within(rows[2]).getByText("Negative")).toBeInTheDocument();
    expect(within(table).queryByText("Third Co")).toBeNull();
    expect(
      screen.getByText(/not investment recommendations/),
    ).toBeInTheDocument();
  });

  it("renders a digest generated before the snapshot existed as before", async () => {
    mockFetchResult.mockResolvedValue({ ...baseResult, items: [item({})] });

    render(<DigestView />);

    expect(await screen.findByText("Example Bio")).toBeInTheDocument();
    expect(screen.queryByText("Executive snapshot")).toBeNull();
    expect(screen.queryByText("Company sentiment summary")).toBeNull();
    expect(screen.queryByRole("table")).toBeNull();
  });

  it("lists items alphabetically by default and by rank on request", async () => {
    mockFetchResult.mockResolvedValue({
      ...baseResult,
      items: [
        item({ rank: 1, company_name: "Zeta Bio", title: "Zeta headline" }),
        item({
          rank: 2,
          company_name: "alpha Pharma",
          title: "Alpha headline",
        }),
        item({ rank: 3, company_name: null, title: "Unnamed headline" }),
        item({ rank: 4, company_name: "Beta Tx", title: "Beta headline" }),
      ],
    });

    render(<DigestView />);
    await screen.findByText("Zeta headline");

    const headlines = () =>
      screen.getAllByText(/ headline$/).map((node) => node.textContent);

    expect(headlines()).toEqual([
      "Alpha headline",
      "Beta headline",
      "Zeta headline",
      "Unnamed headline",
    ]);

    await userEvent.click(screen.getByRole("button", { name: "Rank" }));
    expect(headlines()).toEqual([
      "Zeta headline",
      "Alpha headline",
      "Unnamed headline",
      "Beta headline",
    ]);

    await userEvent.click(screen.getByRole("button", { name: "Company A–Z" }));
    expect(headlines()[0]).toBe("Alpha headline");
  });
});

const buildItem = (overrides: Record<string, unknown> = {}) => ({
  rank: 1,
  catalyst_event_id: "event-1",
  company_name: "Acme Bio",
  company_id: "workspace-1/company/acme_bio",
  title: "Phase 3 readout",
  event_type: "clinical_trial",
  event_date: "2026-09-04",
  summary: "Summary",
  rationale: "Rationale",
  source_url: "https://example.test/a",
  reported_date: "2026-09-05",
  ...overrides,
});

const buildResult = (items: Record<string, unknown>[]) => ({
  id: "result-1",
  workspace_id: "workspace-1",
  digest_config_id: "config-1",
  generated_at: "2026-09-05T09:00:00Z",
  generated_by: "scheduled",
  event_count: items.length,
  items,
  llm_model: "basic",
  llm_prompt_tokens: 1,
  llm_completion_tokens: 1,
  generation_duration_seconds: 1,
  status: "completed",
  error_message: null,
});

// A digest item's company name must link through to the company profile.
// Before this was wired up the name rendered as clickable text with a dead
// click handler, so the only feedback was the hover underline.
describe("DigestView company profile link", () => {
  beforeEach(() => {
    jest.clearAllMocks();
    mockUseWorkspaceContext.mockReturnValue({
      workspace: { id: "workspace-1", name: "Workspace" },
    });
  });

  it("renders the company name as a link to its profile", async () => {
    mockFetchResult.mockResolvedValue(buildResult([buildItem()]));

    render(<DigestView />);

    const link = await screen.findByRole("link", { name: "Acme Bio" });
    expect(link).toHaveAttribute(
      "href",
      "/workspace/workspace-1/bkg/companies/acme_bio?from=catalyst-events",
    );
  });

  it("navigates to the profile carrying the full document id", async () => {
    mockFetchResult.mockResolvedValue(buildResult([buildItem()]));

    render(<DigestView />);

    const link = await screen.findByRole("link", { name: "Acme Bio" });
    await userEvent.click(link);

    await waitFor(() => expect(mockNavigate).toHaveBeenCalled());
    expect(mockNavigate).toHaveBeenCalledWith(
      "/workspace/workspace-1/bkg/companies/acme_bio?from=catalyst-events",
      { state: { documentId: "workspace-1/company/acme_bio" } },
    );
  });

  it("keeps the full document id when the company slug contains a slash", async () => {
    mockFetchResult.mockResolvedValue(
      buildResult([
        buildItem({
          company_name: "AstraZeneca/Daiichi Sankyo",
          company_id: "workspace-1/company/astrazeneca/daiichi_sankyo",
        }),
      ]),
    );

    render(<DigestView />);

    const link = await screen.findByRole("link", {
      name: "AstraZeneca/Daiichi Sankyo",
    });
    // The href itself must carry the full id: Cmd/Ctrl/middle-click and
    // copied links never see the navigation state below.
    expect(link).toHaveAttribute(
      "href",
      "/workspace/workspace-1/bkg/companies/workspace-1%2Fcompany%2Fastrazeneca%2Fdaiichi_sankyo?from=catalyst-events",
    );
    await userEvent.click(link);

    await waitFor(() => expect(mockNavigate).toHaveBeenCalled());
    expect(mockNavigate.mock.calls[0][1]).toEqual({
      state: { documentId: "workspace-1/company/astrazeneca/daiichi_sankyo" },
    });
  });

  it("renders an unlinkable company as plain text, not a dead link", async () => {
    mockFetchResult.mockResolvedValue(
      buildResult([buildItem({ company_id: null })]),
    );

    render(<DigestView />);

    expect(await screen.findByText("Acme Bio")).toBeInTheDocument();
    expect(screen.queryByRole("link", { name: "Acme Bio" })).toBeNull();
  });
});
